import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { SystemTable } from '@sharedCR/common/SystemTable';
import AccordionCDS from '@sharedCR/coreDesignSystem/AccordionCDS';
import CheckboxCDS from '@sharedCR/coreDesignSystem/form/CheckboxCDS';
import { auditApi as loansAuditApi, usersManagementApi } from 'app/services/api';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { DeletedUsersPolicy, USER_DISPLAY_NAME_FIELDS } from 'constants/userSearch';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useSort from 'hooks/useSort';
import useTableFilter from 'hooks/useTableFilter';
import { getConditionalObject } from 'utils/data';
import { getUserDisplayName } from 'utils/user';

import { selectIsClaimAllowed, selectMasterCompanyId } from 'store/auth/selectors/master.selectors';

import AuditRecordDetail from './components/AuditRecordDetail';
import AuditStateMessage from './components/AuditStateMessage';
import { getAuditTableConfig, getHistoryStateMessage } from './EntityAuditHistory.config';
import {
  AUDIT_COLUMN_ID,
  DEFAULT_HISTORY_TITLE,
  DEFAULT_PAGE_SIZE,
  EMPTY_FIELD_LIST,
  getHistoryCountLabel,
  getLoadMoreLabel,
  getShowingCountNote,
  HISTORY_END_OF_LIST_MESSAGE,
  HISTORY_LOADING_MORE_LABEL,
  HISTORY_NO_MATCH_MESSAGE,
  IDLE_EXPANDED_DETAIL,
  IDLE_ROW_SCROLL,
  OPENED_ROW_SCROLL_OPTIONS,
} from './EntityAuditHistory.constants';
import { AuditClientPropType, AuditExtensionPropTypes } from './EntityAuditHistory.propTypes';
import {
  decoratedTableStyles,
  entryDetailStyles,
  footerRowStyles,
  headerRowStyles,
  rootStyles,
} from './EntityAuditHistory.styles';
import { EntityAuditHistoryTestIds } from './EntityAuditHistory.testIds';
import {
  buildActorFilterOptions,
  createMutedRowProps,
  getDetailRequestKey,
  getHistoryErrorState,
  getHistoryReadParams,
  isSystemActor,
  readHistoryPage,
  resolveHiddenFields,
  sortEntriesByTime,
  toOperation,
  withoutFields,
} from './EntityAuditHistory.utils';

const DEFAULT_ORDER = { id: AUDIT_COLUMN_ID.TIME, direction: 'desc' };
const SKELETON_ROWS = 5;

/** Human actor ids on the given rows (System rows have none; on-behalf originators included). */
const collectActorIds = summaries =>
  _.uniq(
    _.flatMap(summaries, summary => [
      isSystemActor(summary) ? null : summary.actorId,
      summary.initiatedByActorId !== summary.actorId ? summary.initiatedByActorId : null,
    ]),
  ).filter(Boolean);

const EntityAuditHistory = ({
  entityType,
  entityId,
  title = DEFAULT_HISTORY_TITLE,
  collapsible = true,
  defaultCollapsed = true,
  pageSize = DEFAULT_PAGE_SIZE,
  apiBasePath = null,
  auditClient = loansAuditApi,
  renderers = null,
  fieldRenderers = null,
  hiddenFields = EMPTY_FIELD_LIST,
  snapshotExcludedFields = EMPTY_FIELD_LIST,
  getRowDecoration = null,
  shadowedToggle = null,
  renderRowActions = null,
  renderDetailExtras = null,
  changePanels = null,
  isSnapshotHidden = false,
  automaticFields = EMPTY_FIELD_LIST,
}) => {
  const canView = useSelector(selectIsClaimAllowed(COMPOSED_CLAIMS.AUDIT_VIEW));
  const companyId = useSelector(selectMasterCompanyId);

  const [entries, setEntries] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isUnavailable, setIsUnavailable] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [hasOpenedSection, setHasOpenedSection] = useState(false);
  const [includeShadowed, setIncludeShadowed] = useState(false);
  const [actorNames, setActorNames] = useState({});
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [expandedDetail, setExpandedDetail] = useState(IDLE_EXPANDED_DETAIL);
  const [rowScroll, setRowScroll] = useState(IDLE_ROW_SCROLL);

  const requestedActorIdsRef = useRef(new Set());
  // A re-read (toggle, retry) supersedes any page still in flight; a stale response is dropped.
  const historyRequestIdRef = useRef(0);

  const { order, onRequestSort } = useSort(DEFAULT_ORDER);
  const { columnFilters, onColumnFiltersChange, applyFilters } = useTableFilter();

  // Resolve Human actor ids to display names via the existing user-lookup (§3). Best-effort and
  // additive: the row shows a shortened id until the name lands, and a failed lookup never breaks
  // the timeline. Each id is requested once.
  const resolveActorNames = useCallback(
    async summaries => {
      if (!companyId) {
        return;
      }

      const ids = collectActorIds(summaries).filter(id => !requestedActorIdsRef.current.has(id));

      if (!ids.length) {
        return;
      }

      ids.forEach(id => requestedActorIdsRef.current.add(id));

      try {
        const users = await usersManagementApi.searchUsersByIds({
          companyId,
          ids,
          fields: USER_DISPLAY_NAME_FIELDS,
          deletedPolicy: DeletedUsersPolicy.INCLUDE_DELETED,
        });

        const resolved = users.reduce((acc, user) => {
          const name = getUserDisplayName(user);

          return name ? { ...acc, [user.id]: name } : acc;
        }, {});

        if (!_.isEmpty(resolved)) {
          setActorNames(previous => ({ ...previous, ...resolved }));
        }
      } catch {
        // Let the ids be re-requested on a later load rather than stay permanently unresolved.
        ids.forEach(id => requestedActorIdsRef.current.delete(id));
      }
    },
    [companyId],
  );

  const loadHistory = useCallback(async () => {
    historyRequestIdRef.current += 1;

    const requestId = historyRequestIdRef.current;

    setIsLoading(true);
    setIsLoadingMore(false);
    setIsUnavailable(false);
    setHasFailed(false);

    try {
      const { data } = await auditClient.getEntityAuditHistory({
        entityType,
        entityId,
        page: 1,
        pageSize,
        basePath: apiBasePath,
        ...getHistoryReadParams(includeShadowed),
      });

      if (requestId !== historyRequestIdRef.current) {
        return;
      }

      const result = readHistoryPage(data);

      setEntries(result.items);
      setPage(result.page);
      setTotalCount(result.totalCount);
      resolveActorNames(result.items);
    } catch (error) {
      if (requestId !== historyRequestIdRef.current) {
        return;
      }

      const { isUnavailable: unavailable, hasFailed: failed } = getHistoryErrorState(error);

      setEntries([]);
      setTotalCount(0);
      setIsUnavailable(unavailable);
      setHasFailed(failed);
    }

    setIsLoading(false);
    setHasFetched(true);
  }, [
    auditClient,
    entityType,
    entityId,
    pageSize,
    apiBasePath,
    includeShadowed,
    resolveActorNames,
  ]);

  const loadMore = useCallback(async () => {
    const requestId = historyRequestIdRef.current;

    setIsLoadingMore(true);

    try {
      const nextPage = page + 1;

      const { data } = await auditClient.getEntityAuditHistory({
        entityType,
        entityId,
        page: nextPage,
        pageSize,
        basePath: apiBasePath,
        ...getHistoryReadParams(includeShadowed),
      });

      // A page of a list the reader has since re-read (e.g. toggled) must not be appended to it.
      if (requestId === historyRequestIdRef.current) {
        const result = readHistoryPage(data);

        // Append and de-duplicate by auditId: offset paging can shift a boundary row when a new
        // change lands at the head between fetches, and a retry must not double a row.
        setEntries(previous => _.uniqBy([...previous, ...result.items], 'auditId'));
        setPage(nextPage);
        setTotalCount(result.totalCount);
        resolveActorNames(result.items);
      }
    } catch {
      // A load-more failure leaves the already-listed rows intact; nothing destructive to show.
    }

    if (requestId === historyRequestIdRef.current) {
      setIsLoadingMore(false);
    }
  }, [
    auditClient,
    entityType,
    entityId,
    page,
    pageSize,
    apiBasePath,
    includeShadowed,
    resolveActorNames,
  ]);

  const onToggleSection = useCallback((event, expanded) => {
    if (expanded) {
      setHasOpenedSection(true);
    }
  }, []);

  // Changing the flag re-keys loadHistory, so the fetch effect re-reads page 1 with it.
  const onToggleShadowed = useCallback((event, checked) => {
    setIncludeShadowed(checked);
    setEntries([]);
    setPage(1);
    setTotalCount(0);
    setExpandedRowId(null);
    setIsLoading(true);
    setExpandedDetail(IDLE_EXPANDED_DETAIL);
  }, []);

  const loadExpandedDetail = useCallback(
    async auditId => {
      const requestKey = getDetailRequestKey(auditId, includeShadowed);

      setExpandedDetail({ requestKey, isLoading: true, detail: null, isUnavailable: false });

      try {
        const { data } = await auditClient.getEntityAuditRecordDetail({
          auditId,
          basePath: apiBasePath,
          ...getHistoryReadParams(includeShadowed),
        });

        // Expanding another row (or toggling) meanwhile makes this response belong to no open row.
        setExpandedDetail(previous =>
          previous.requestKey === requestKey
            ? { requestKey, isLoading: false, detail: data, isUnavailable: false }
            : previous,
        );
      } catch {
        // Any non-200 (404 not found, 500 missing-blob) degrades to the quiet "Details unavailable"
        // state with a retry — never a crash or a red toast (API Contract §5.3).
        setExpandedDetail(previous =>
          previous.requestKey === requestKey
            ? { requestKey, isLoading: false, detail: null, isUnavailable: true }
            : previous,
        );
      }
    },
    [auditClient, apiBasePath, includeShadowed],
  );

  const expandRow = useCallback(
    rowId => {
      setExpandedRowId(rowId);
      loadExpandedDetail(rowId);
    },
    [loadExpandedDetail],
  );

  // A row the user opens or closes themselves is not scrolled to: only `openRow` asks for that.
  const onToggleExpand = useCallback(
    rowId => {
      setRowScroll(previous => ({ ...previous, rowId: null }));

      if (expandedRowId === rowId) {
        setExpandedRowId(null);

        return;
      }

      expandRow(rowId);
    },
    [expandedRowId, expandRow],
  );

  // A row action's way back to its row: expanded (read once) and scrolled into view.
  const openRow = useCallback(
    rowId => {
      if (expandedRowId !== rowId) {
        expandRow(rowId);
      }

      setRowScroll(previous => ({ rowId, request: previous.request + 1 }));
    },
    [expandedRowId, expandRow],
  );

  // Each scroll request makes a new ref, so React hands it the opened row's detail again.
  const scrollToOpenedRow = useCallback(
    node => {
      if (node && rowScroll.rowId === expandedRowId) {
        node.scrollIntoView?.(OPENED_ROW_SCROLL_OPTIONS);
      }
    },
    [rowScroll, expandedRowId],
  );

  const onRetryExpandedDetail = useCallback(
    () => loadExpandedDetail(expandedRowId),
    [loadExpandedDetail, expandedRowId],
  );

  const rowsWithId = useMemo(
    () =>
      entries.map(summary => {
        const rowHiddenFields = resolveHiddenFields(hiddenFields, summary);

        return {
          ...summary,
          id: summary.auditId,
          ...getConditionalObject(!_.isEmpty(rowHiddenFields), () => ({
            changedFields: withoutFields(summary.changedFields, rowHiddenFields),
          })),
        };
      }),
    [entries, hiddenFields],
  );

  const actorFilterOptions = useMemo(
    () => buildActorFilterOptions(entries, actorNames),
    [entries, actorNames],
  );

  const rowActionsContext = useMemo(() => ({ rows: rowsWithId, openRow }), [rowsWithId, openRow]);

  const tableConfig = useMemo(
    () =>
      getAuditTableConfig({
        expandedRowId,
        onToggleExpand,
        actorNames,
        actorFilterOptions,
        getRowDecoration,
        renderRowActions,
        rowActionsContext,
      }),
    [
      expandedRowId,
      onToggleExpand,
      actorNames,
      actorFilterOptions,
      getRowDecoration,
      renderRowActions,
      rowActionsContext,
    ],
  );

  const getRowProps = useMemo(() => createMutedRowProps(getRowDecoration), [getRowDecoration]);

  // Filter + sort are client-side over the loaded rows (the host pages, it does not filter): the
  // header's column filters narrow the rows, and the Time column sorts them.
  const rows = useMemo(() => {
    const filtered = applyFilters(rowsWithId, tableConfig);

    return order.id === AUDIT_COLUMN_ID.TIME
      ? sortEntriesByTime(filtered, order.direction)
      : filtered;
  }, [applyFilters, rowsWithId, tableConfig, order]);

  const expandedSummary = useMemo(
    () => rowsWithId.find(row => row.id === expandedRowId) ?? null,
    [rowsWithId, expandedRowId],
  );

  // A per-row rule reads the row as the host sent it, before its own hidden fields are stripped,
  // and the drill-in's detail once it is read.
  const expandedHiddenFields = useMemo(
    () =>
      resolveHiddenFields(
        hiddenFields,
        entries.find(summary => summary.auditId === expandedRowId),
        expandedDetail.detail,
      ),
    [hiddenFields, entries, expandedRowId, expandedDetail.detail],
  );

  const expandedRowContent = useMemo(() => {
    if (!expandedRowId) {
      return null;
    }

    return (
      <Box ref={scrollToOpenedRow} sx={entryDetailStyles}>
        <AuditRecordDetail
          actorNames={actorNames}
          auditId={expandedRowId}
          automaticFields={automaticFields}
          changePanels={changePanels}
          detail={expandedDetail.detail}
          fieldRenderers={fieldRenderers}
          hiddenFields={expandedHiddenFields}
          isLoading={expandedDetail.isLoading}
          isSnapshotHidden={isSnapshotHidden}
          isUnavailable={expandedDetail.isUnavailable}
          operation={expandedSummary ? toOperation(expandedSummary.operation) : null}
          renderDetailExtras={renderDetailExtras}
          renderers={renderers}
          row={expandedSummary}
          snapshotExcludedFields={snapshotExcludedFields}
          onRetry={onRetryExpandedDetail}
        />
      </Box>
    );
  }, [
    expandedRowId,
    actorNames,
    expandedDetail,
    expandedSummary,
    expandedHiddenFields,
    renderDetailExtras,
    renderers,
    fieldRenderers,
    snapshotExcludedFields,
    changePanels,
    isSnapshotHidden,
    automaticFields,
    onRetryExpandedDetail,
    scrollToOpenedRow,
  ]);

  // When not collapsible (e.g. hosted in a drawer whose own title frames it), there is no accordion
  // to expand, so the timeline loads on mount; collapsed-by-default sections wait for first expand.
  const fetchesOnMount = !collapsible || !defaultCollapsed;
  const shouldFetch = fetchesOnMount || hasOpenedSection;

  useEffectSafeStrictMode(() => {
    if (canView && shouldFetch) {
      loadHistory();
    }
  }, [canView, shouldFetch, loadHistory]);

  if (!canView) {
    return null;
  }

  const hasEntries = entries.length > 0;
  const stateMessage = getHistoryStateMessage({
    isLoading,
    isUnavailable,
    hasFailed,
    isEmpty: hasFetched && !hasEntries,
  });
  const canLoadMore = hasEntries && entries.length < totalCount;
  const loadMoreCount = Math.min(pageSize, Math.max(totalCount - entries.length, 0));

  const body = (
    <Box data-testid={EntityAuditHistoryTestIds.ROOT} sx={rootStyles}>
      <CheckboxCDS
        dataTestId={EntityAuditHistoryTestIds.SHADOWED_TOGGLE}
        disabled={isLoading}
        isHidden={!shadowedToggle || isUnavailable}
        label={shadowedToggle?.label}
        value={includeShadowed}
        onChange={onToggleShadowed}
      />

      {!!stateMessage && (
        <AuditStateMessage
          dataTestId={stateMessage.dataTestId}
          isLoading={stateMessage.isLoading}
          message={stateMessage.message}
          retryTestId={stateMessage.retryTestId}
          onRetry={stateMessage.hasRetry ? loadHistory : null}
        />
      )}

      {hasEntries && (
        <>
          <Box data-testid={EntityAuditHistoryTestIds.HEADER} sx={headerRowStyles}>
            <Typography
              color="text.secondary"
              data-testid={EntityAuditHistoryTestIds.HEADER_COUNT}
              variant="regular14"
            >
              {getHistoryCountLabel(totalCount)}
            </Typography>
          </Box>

          <SystemTable
            additionalRowContent={expandedRowContent}
            additionalRowContentRowId={expandedRowId}
            columnFilters={columnFilters}
            config={tableConfig}
            customStyles={getRowDecoration ? decoratedTableStyles : null}
            data={rows}
            getRowProps={getRowProps}
            noAvailableMessageText={HISTORY_NO_MATCH_MESSAGE}
            order={order}
            skeletonRows={SKELETON_ROWS}
            testId={EntityAuditHistoryTestIds.TABLE}
            onColumnFiltersChange={onColumnFiltersChange}
            onRequestSort={onRequestSort}
          />

          <Box sx={footerRowStyles}>
            {canLoadMore ? (
              <Button
                data-testid={EntityAuditHistoryTestIds.LOAD_MORE}
                disabled={isLoadingMore}
                size="small"
                startIcon={isLoadingMore ? <CircularProgress size={14} /> : null}
                variant="outlined"
                onClick={loadMore}
              >
                {isLoadingMore ? HISTORY_LOADING_MORE_LABEL : getLoadMoreLabel(loadMoreCount)}
              </Button>
            ) : (
              <Typography
                color="text.secondary"
                data-testid={EntityAuditHistoryTestIds.END_OF_LIST}
                variant="regular12"
              >
                {HISTORY_END_OF_LIST_MESSAGE}
              </Typography>
            )}

            <Typography
              color="text.secondary"
              data-testid={EntityAuditHistoryTestIds.SHOWING_NOTE}
              variant="regular12"
            >
              {getShowingCountNote(entries.length, totalCount)}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );

  if (!collapsible) {
    return body;
  }

  return (
    <AccordionCDS
      defaultExpanded={!defaultCollapsed}
      title={title}
      titleVariant="bold16"
      onChange={onToggleSection}
    >
      {body}
    </AccordionCDS>
  );
};

EntityAuditHistory.propTypes = {
  entityType: PropTypes.string.isRequired,
  entityId: PropTypes.string.isRequired,
  title: PropTypes.string,
  collapsible: PropTypes.bool,
  defaultCollapsed: PropTypes.bool,
  pageSize: PropTypes.number,
  apiBasePath: PropTypes.string,
  auditClient: AuditClientPropType,
  ...AuditExtensionPropTypes,
};

export default memo(EntityAuditHistory);
